"use client";
import { InfoTooltip } from "@/ui/index";
import { ExternalLink } from "@cmp-src/primitives/external-link";
import { TimeZoneMismatchAlert } from "@/features/settings/components/TimeZoneMismatchAlert";
import { ReservationRecurringEditConfirm } from "@/features/reserve/components/ReservationRecurringEditConfirm";
import { ReserveFieldDescription } from "@/features/reserve/components/ReserveFieldDescription/ReserveFieldDescription";
import { ReserveFieldDuration } from "@/features/reserve/components/ReserveFieldDuration/ReserveFieldDuration";
import { ReserveFieldOrganizer } from "@/features/reserve/components/ReserveFieldOrganizer/ReserveFieldOrganizer";
import { ReserveFieldParkingList } from "@/features/reserve/components/ReserveFieldParkingList/ReserveFieldParkingList";
import { ReserveFieldRecurring } from "@/features/reserve/components/ReserveFieldRecurring/ReserveFieldRecurring";
import { ReserveFieldResourcesList } from "@/features/reserve/components/ReserveFieldResourcesList/ReserveFieldResourcesList";
import { ReserveFieldStartDate } from "@/features/reserve/components/ReserveFieldStartDate/ReserveFieldStartDate";
import { ReserveFieldTime } from "@/features/reserve/components/ReserveFieldTime/ReserveFieldTime";
import { ReserveFieldTitle } from "@/features/reserve/components/ReserveFieldTitle/ReserveFieldTitle";
import { getReservationExtensionDays } from "@gettactic/helpers/src/resources/defines";
import { getEditedReservationAttendeeIds } from "@/features/reserve/utils/getEditedReservationAttendeeIds";
import { ReserveFieldUsers } from "@/features/reserve/components/ReserveFieldUsers/ReserveFieldUsers";
import { ReserveFieldOnlineMeeting } from "@/features/reserve/components/ReserveFieldOnlineMeeting/ReserveFieldOnlineMeeting";
import { ReserveEditFormWPRequests } from "@/features/reserve/components/ReserveEdit/controls/ReserveEditFormWPRequests";
import { Form } from "@/ui/form";
import { FloorFilterDropdown } from "@/ui/_to_replace/FloorFilterDropdown";
import { LoadingSpinner } from "@gettactic/components/src/loadings/LoadingSpinner/LoadingSpinner";
import { useI18n } from "@/lib/hooks/useI18n";
import { useReserveResources } from "@/features/reserve/hooks/useReserveResources";
import { api } from "@/lib/api/api";
import { cn } from "@gettactic/helpers/src/css/cn";
import { showRecurring } from "@gettactic/helpers/src/featureFlags";
import { canScheduleOthers } from "@/lib/permissions";
import { getEditedReservationExtraRooms } from "@/features/reserve/utils/getEditedReservationExtraRooms";
import { getEditedReservationPrimaryResource } from "@/features/reserve/utils/getEditedReservationPrimaryResource";
import {
  convertUTCToLocalDate,
  getTimeForReservation,
  primaryScheduleResource,
} from "@/lib/utils";
import {
  SaveReservationData,
  editReservations,
  getDurationOptions,
  noParkingResource,
  recurringToRRule,
  saveReservations,
} from "@/features/reserve/utils/utils-reserve";
import { isValidEmail } from "@/lib/validation/email";
import { ISchedule, Resource, isParking } from "@gettactic/api";
import { useFloors } from "@gettactic/hooks";
import { useAuthenticated } from "@gettactic/hooks";
import { PencilAltIcon } from "@heroicons/react/solid";
import { TrashIcon } from "@heroicons/react/solid";
import { useQueryClient } from "@tanstack/react-query";
import { addDays, differenceInMinutes, format } from "date-fns";
import { useEffect, useMemo, useState, useRef } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { WorkplaceRequestsRef } from "@/types/workplace-requests";
import { Label } from "@cmp-src/primitives/label";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { Button } from "@cmp-src/primitives/button";

interface ReserveEditProps {
  schedule: ISchedule;
  onClose: () => void;
  isLoading?: boolean;
  floorId?: null | string;
  canRespondCancel?: boolean;
  respondCancel?: () => void;
  onRoomChange: (roomId: string) => void;
  onOrganizerChange?: (organizerId: string | null) => void;
  organizerRequiresDelegatedAccess?: boolean;
  hasMSCalendar?: boolean;
  hasGoogleCalendar?: boolean;
}

export const ReserveEdit = ({
  schedule,
  onClose,
  floorId = schedule.resource.parent_id,
  canRespondCancel,
  respondCancel,
  isLoading,
  onRoomChange,
  onOrganizerChange,
  organizerRequiresDelegatedAccess = false,
  hasMSCalendar,
  hasGoogleCalendar,
}: ReserveEditProps) => {
  const {
    userContext: { authenticatedUser },
  } = useAuthenticated();
  const { templates } = useI18n(authenticatedUser);

  const [confirmFunction, setConfirmFunction] = useState<
    null | ((x: "this" | "future" | "all") => void)
  >(null);
  const offices = authenticatedUser.offices;
  const queryClient = useQueryClient();
  const [filterFloor, setFilterFloor] = useState<string | null>(floorId);
  const [loading, setLoading] = useState<boolean>(isLoading || false);
  const [notesOpened, setNotesOpened] = useState(false);
  const wpRequestsRef = useRef<WorkplaceRequestsRef>(null);
  const primaryResource = getEditedReservationPrimaryResource(
    schedule,
    primaryScheduleResource(schedule.slots),
  );

  // const { calendar } = useCalendarByResource(primaryResource.id);
  const entity = primaryResource.type;
  const [error, setError] = useState("");
  const isMeetingRoom = entity === "meeting_room";
  const isPrimaryParking =
    entity === "parking_space" || entity === "parking_lot";
  const parkingSlot = schedule.slots.find((x) => isParking(x.resource));
  const startOfficeDate: Date = convertUTCToLocalDate(schedule.start);
  const endOfficeDate: Date = convertUTCToLocalDate(schedule.end);
  const office = offices?.officeId
    ? offices?.byId[primaryResource.office_id]
    : undefined;
  const { floors } = useFloors(office?.id ?? "");
  const currentOfficeTz = office?.time_zone ?? "";
  const users = getEditedReservationAttendeeIds(schedule, isPrimaryParking);
  const allowOthers = canScheduleOthers(authenticatedUser);

  const defaultValues = {
    resource_id: primaryResource.id,
    parking_resource_id: parkingSlot ? parkingSlot.resource.id : null,
    start: startOfficeDate,
    time: getTimeForReservation(
      startOfficeDate,
      startOfficeDate,
      office || null,
    ),
    duration: differenceInMinutes(endOfficeDate, startOfficeDate),
    teams: [],
    users,
    title: schedule.title,
    description: schedule.description,
    online_meeting: schedule.online_meeting,
  };

  if (!defaultValues.start || !(defaultValues.start instanceof Date)) {
    defaultValues.start = new Date();
  }
  const weekDates = useMemo<Date[]>(
    () => [defaultValues.start],
    [defaultValues.start],
  );
  const weekDatesStr = weekDates.map((weekDate) =>
    format(weekDate, "yyyy-MM-dd"),
  );
  const [startTime, setStartTime] = useState<Date>(() => defaultValues.time);
  const [durationMin, setDurationMin] = useState<number>(
    () => defaultValues.duration,
  );
  // A new array on every render makes ReserveFieldDuration re-sync the
  // duration into the form on every render: an endless render loop.
  const durationOptions = useMemo(
    () => getDurationOptions(startTime),
    [startTime]
  );

  const defaultOrganizer = useMemo(() => {
    const organizerSlot = schedule.slots.find(
      (x) => x.user.id === schedule.organizer_id,
    );
    return organizerSlot
      ? { value: organizerSlot.user.id, label: organizerSlot.user.name }
      : undefined;
  }, [schedule]);

  const form = useForm<SaveReservationData>({
    defaultValues: {
      title: defaultValues.title ?? "",
      weekDates: [defaultValues.start],
      description: defaultValues.description ?? "",
      organizer: defaultOrganizer,
      time: defaultValues.time,
      duration: {
        value: defaultValues.duration,
      } as SaveReservationData["duration"],
      users: users.map((u) => ({ value: u })),
      teams: [],
      online_meeting: defaultValues.online_meeting?.provider
        ? {
            provider: defaultValues.online_meeting.provider,
          }
        : null,
    },
  });

  const organizer = form.watch("organizer") ?? defaultOrganizer;

  // The organizer's delegated access is what the backend checks when saving a room
  const roomOrganizerId = isMeetingRoom ? (organizer?.value ?? null) : null;
  useEffect(() => {
    onOrganizerChange?.(isValidEmail(roomOrganizerId) ? null : roomOrganizerId);
  }, [roomOrganizerId, onOrganizerChange]);

  const userIdForResources = isMeetingRoom
    ? ((allowOthers ? organizer?.value : null) ?? null)
    : users[0];
  const { resourcesList: resources, parkingResourcesList: parkings } =
    useReserveResources({
      resourceType: entity,
      officeId: primaryResource.office_id,
      durationMins: durationMin,
      startTime,
      weekDatesStr,
      organizer: isValidEmail(userIdForResources) ? null : userIdForResources,
    });

  /**
   * We should add the current resource
   * because will not available due that is
   * already reserved.
   */
  const resourcesList = useMemo(() => {
    const defaultResource = { ...primaryResource, label: primaryResource.name };
    if (!resources) {
      return [defaultResource as Resource];
    }
    const has = resources.find((x) => x.id === primaryResource.id);
    if (has) {
      return resources;
    }
    return resources.concat([defaultResource]);
  }, [resources, schedule]);

  const parkingResourcesList = useMemo(() => {
    const defaultParking = parkingSlot?.resource;
    if (!defaultParking) {
      return parkings;
    }
    if (!parkings) {
      return [defaultParking];
    }
    const has = parkings.find((x) => x.id === defaultParking.id);
    if (has) {
      return parkings;
    }
    return parkings.concat([defaultParking]);
  }, [parkings, parkingSlot]);

  const defaultResource =
    defaultValues.resource_id && resourcesList
      ? (resourcesList.filter(
          (resource) => resource.id === defaultValues.resource_id,
        )[0] ?? null)
      : null;
  const defaultParking = useMemo(() => {
    return (
      parkingResourcesList?.find(
        (x) => x.id === defaultValues.parking_resource_id,
      ) ?? noParkingResource
    );
  }, [parkingResourcesList, defaultValues, noParkingResource]);

  const originalValues = {
    ...defaultValues,
    duration: {
      value: defaultValues.duration,
    },
    users: users.map((u) => {
      return { value: u };
    }),
    weekDates: [defaultValues.start],
    organizer: defaultOrganizer,
    resource: defaultResource,
    parking_resource: defaultParking,
    recurring: recurringToRRule(schedule).toString(),
    online_meeting: defaultValues.online_meeting?.provider
      ? {
          provider: defaultValues.online_meeting.provider,
        }
      : null,
  } as SaveReservationData;

  // Set resource and parking_resource on the form once they load
  useEffect(() => {
    if (defaultResource && !form.getValues("resource")) {
      form.setValue("resource", defaultResource);
    }
  }, [defaultResource, form]);

  useEffect(() => {
    if (defaultParking && !form.getValues("parking_resource")) {
      form.setValue("parking_resource", defaultParking);
    }
  }, [defaultParking, form]);

  const currentResource = form.watch("resource");
  useEffect(() => {
    if (currentResource?.id) {
      onRoomChange(currentResource.id);
    }
  }, [currentResource?.id, onRoomChange]);

  const onSubmit: SubmitHandler<SaveReservationData> = async (data, ev) => {
    ev?.stopPropagation();
    ev?.preventDefault();
    // The backend rejects it, and Enter can submit even with the button disabled
    if (organizerRequiresDelegatedAccess) {
      return;
    }

    // Ensure resource is set — it loads async via the Controller
    if (!data.resource && defaultResource) {
      data.resource = defaultResource;
    }
    if (!data.parking_resource) {
      data.parking_resource = defaultParking;
    }

    const callback = async (value: "this" | "future" | "all") => {
      setLoading(true);
      const recurringSchedule =
        schedule.recurring_schedule_id &&
        (value === "future" || value === "all")
          ? await api.client.schedules.schedulesById(
              [schedule.recurring_schedule_id],
              primaryResource.office_id,
              {
                schedule_types: ["recurring"],
                slot_types: ["recurring"],
              },
            )
          : null;

      const scheduleToEdit = recurringSchedule?.result?.elements[0]
        ? recurringSchedule?.result?.elements[0]
        : schedule;

      // If we are editing a recurring schedule, there is a chance that the instance differs from the recurring template
      // In this case, we need to check the delta between the three data sets. The easiest way to do this
      // is by filtering out the existing schedule instance user's from the form data and then combining the filtered
      // data with the recurring template users. This will give us the delta between the instance and the template.
      if (
        value === "future" &&
        recurringSchedule &&
        recurringSchedule?.result?.elements[0]
      ) {
        const recurringTemplateUsers =
          recurringSchedule.result.elements[0].slots.map((x) => ({
            value: x.user.id,
          }));
        const instanceUsers = schedule.slots.map((x) => ({ value: x.user.id }));
        const differingUsers = instanceUsers.filter(
          (userObj) =>
            !recurringTemplateUsers.map((x) => x.value).includes(userObj.value),
        );
        data.users = data.users.filter(
          (userObj) =>
            !differingUsers.map((x) => x.value).includes(userObj.value),
        );
      }

      const isFirstOccurrence =
        format(originalValues.time, "yyyy-MM-dd") ===
        scheduleToEdit.recurring_task?.start?.split("T")[0];
      let hasError = false;
      let message: string | undefined;

      // Edit the current schedule and all the future ones, to do that we split
      // the schedule into two parts, changing the current one end time and creating
      // a new one for the future. Do this only if the schedule being edited is not
      // the first occurrence, in that case we just edit everything, there's no need
      // to create a new schedule

      const onlineMeeting = form.getValues("online_meeting");
      const isProviderUpdated =
        onlineMeeting?.provider &&
        onlineMeeting.provider !== defaultValues.online_meeting?.provider;

      const isOnlineMeetingTurnedOff =
        !onlineMeeting?.provider && !!defaultValues.online_meeting?.provider;
      const onlineMeetingData = isProviderUpdated
        ? {
            online_meeting: {
              provider: onlineMeeting.provider,
            },
          }
        : isOnlineMeetingTurnedOff
          ? { online_meeting: null }
          : // if online meeting is not changed and not turned off, don't send anything
            { online_meeting: undefined };

      if (value === "future" && !isFirstOccurrence) {
        const newSchedule = { ...data, organizer, ...onlineMeetingData };

        // Edit the current schedule with the new end time
        const newEnd = addDays(newSchedule.time, -1);
        scheduleToEdit.recurring_task!.end = `${format(newEnd, "yyyy-MM-dd")}T23:59:59.000Z`;
        const editResult = await editReservations(
          scheduleToEdit,
          {
            ...originalValues,
            recurring: recurringToRRule(scheduleToEdit).toString(),
            organizer,
            ...onlineMeetingData,
          },
          true,
          templates.displayDate,
          authenticatedUser.slug ?? "",
        );
        if (editResult.hasError && editResult.message) {
          setLoading(false);
          setError(editResult.message);
          return;
        }
        const saveResult = await saveReservations(newSchedule, {
          organizerId: newSchedule.organizer.value,
          isMeetingRoom: isMeetingRoom,
          // The future series is created from scratch, so it has to be handed the
          // reservation's other rooms or they are dropped from every occurrence.
          rooms: isMeetingRoom
            ? getEditedReservationExtraRooms(
                scheduleToEdit,
                newSchedule.resource?.id ?? "",
              )
            : undefined,
          authUserId: authenticatedUser.user!.id,
          office: office,
          allowOthers: allowOthers,
          timeZone: office?.time_zone || "",
          officeId: office?.id || "",
          noParkingResource: noParkingResource,
          dateFormat: templates.displayDate,
          orgSlug: authenticatedUser.slug ?? "",
          ...onlineMeetingData,
        });
        hasError = saveResult.hasError;
        message = saveResult.message;
      } else if (value === "all" || (value === "future" && isFirstOccurrence)) {
        const result = await editReservations(
          scheduleToEdit,
          { ...data, organizer, ...onlineMeetingData },
          true,
          templates.displayDate,
          authenticatedUser.slug ?? "",
        );
        hasError = result.hasError;
        message = result.message;
      } else {
        const result = await editReservations(
          scheduleToEdit,
          { ...data, organizer, ...onlineMeetingData },
          false,
          templates.displayDate,
          authenticatedUser.slug ?? "",
        );
        hasError = result.hasError;
        message = result.message;
      }

      if (wpRequestsRef.current) {
        const userIdForResources = allowOthers
          ? isMeetingRoom
            ? (organizer?.value ?? null)
            : users[0]
          : null;
        try {
          const result = await wpRequestsRef.current.submitWorkplaceRequests({
            scheduleId: schedule.id,
            ownerUserId: userIdForResources ?? authenticatedUser.user?.id,
          });
          if (!result.success) {
            // Log error but don't block the reservation success
            console.warn("Workplace requests submission failed:", result.error);
          }
        } catch (error) {
          // Log error but don't block the reservation success
          console.warn("Workplace requests submission error:", error);
        }
      }

      setLoading(false);
      if (hasError && message) {
        setError(message);
      } else {
        await queryClient.invalidateQueries({
          queryKey: ["schedules"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["myAgenda"],
          exact: false,
        });
        await queryClient.invalidateQueries({
          queryKey: ["organizations.schedulable"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["schedule", schedule.id],
        });
        await queryClient.invalidateQueries({
          queryKey: ["tableOverview"],
        });

        onClose();
      }
    };

    // If we are editing a recurring schedule, we need to confirm if the user wants to
    // edit this instance only or all future instances. This mimics Google Calendar.
    if (schedule.recurring_schedule_id) {
      setConfirmFunction(() => callback);
    } else {
      await callback("this");
    }
  };

  const enableFloorSelector = (floors?.length ?? 0) > 1;

  return (
    <TooltipProvider>
      <div className="text-left">
        {office ? (
          <TimeZoneMismatchAlert
            onClose={onClose}
            className="mb-5 max-w-xl"
            timezone={currentOfficeTz}
            officeId={office.id}
          />
        ) : null}
        <Form {...form}>
          <form className="mt-2 flex" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-1 flex-col">
              {isMeetingRoom ? (
                <div className="mb-2">
                  <ReserveFieldTitle form={form} showLabel={false} />
                </div>
              ) : null}

              <div className="mb-2">
                <Label htmlFor="dates">Date & time</Label>
              </div>
              <div
                className={cn(
                  "mb-2",
                  enableFloorSelector || isMeetingRoom
                    ? "grid grid-cols-7 gap-2"
                    : null,
                )}
              >
                <div className="col-span-3 min-w-0">
                  {office ? (
                    <ReserveFieldStartDate
                      form={form}
                      defaultValue={weekDates}
                      startTime={startTime}
                      weekDates={weekDates}
                      officeTimeZone={currentOfficeTz}
                      errors={form.formState.errors}
                      resType={entity}
                      office={office}
                      isAdmin={allowOthers}
                      extraDays={getReservationExtensionDays(
                        authenticatedUser?.user?.email,
                      )}
                    />
                  ) : null}
                </div>
                <div className="col-span-2 min-w-0">
                  <ReserveFieldTime
                    form={form}
                    defaultValue={defaultValues.time}
                    office={office || null}
                    errors={form.formState.errors}
                    setStartTime={setStartTime}
                    weekDates={weekDates}
                  />
                </div>
                <div className="col-span-2 min-w-0 text-left">
                  <ReserveFieldDuration
                    startTime={startTime}
                    durationOptions={durationOptions}
                    form={form}
                    defaultValue={defaultValues.duration}
                    setDurationMin={setDurationMin}
                    errors={form.formState.errors}
                  />
                </div>
              </div>

              <div className="text-left">
                {showRecurring(entity, authenticatedUser, office!) ? (
                  <ReserveFieldRecurring
                    errors={form.formState.errors}
                    defaultValue={recurringToRRule(schedule).toString()}
                    form={form}
                    office={office}
                    resourceType={entity}
                    originalSchedule={schedule}
                  />
                ) : null}
              </div>

              {/* Online Meeting Field for Meeting Rooms */}
              {isMeetingRoom && (
                <div className="my-2">
                  <ReserveFieldOnlineMeeting
                    isEdit={true}
                    form={form}
                    isMeetingRoom={isMeetingRoom}
                    hasMSCalendar={hasMSCalendar}
                    hasGoogleCalendar={hasGoogleCalendar}
                  />

                  {/* Status-specific UI components */}
                  {schedule.online_meeting && (
                    <>
                      {/* Success status - show join link */}
                      {schedule.online_meeting.status === "success" &&
                        schedule.online_meeting.url && (
                          <div className="mt-3 rounded-md bg-green-50 p-3 border border-green-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full" />
                                <span className="text-sm font-medium text-green-800">
                                  Online meeting link available
                                </span>
                              </div>
                              <ExternalLink
                                href={schedule.online_meeting.url}
                                className="text-sm text-green-600 hover:text-green-800 underline font-normal"
                              >
                                Join Meeting
                              </ExternalLink>
                            </div>
                          </div>
                        )}

                      {/* Pending status - show pending message */}
                      {schedule.online_meeting.status === "pending" && (
                        <div className="mt-3 rounded-md border-neutral-200 bg-neutral-50 p-3 border">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-neutral-500 rounded-full" />
                            <span className="text-sm text-neutral-800">
                              Online meeting link is being created. Please check
                              back soon.
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Error status - show error message */}
                      {schedule.online_meeting.status === "error" && (
                        <div className="mt-3 rounded-md bg-red-50 p-3 border border-red-200">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full" />
                            <span className="text-sm font-medium text-red-800">
                              Failed to create online meeting link
                            </span>
                          </div>
                          <p className="text-sm text-red-600 mt-1">
                            Something went wrong and the link wasn't created.
                            Please try again.
                          </p>
                        </div>
                      )}

                      {/* Warning when turning off online meeting with existing link */}
                      {schedule.online_meeting.status === "success" &&
                        schedule.online_meeting.url &&
                        !form.watch("online_meeting.provider") && (
                          <div className="mt-3 rounded-md bg-yellow-50 p-3 border border-yellow-200">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                              <span className="text-sm font-medium text-yellow-800">
                                Warning
                              </span>
                            </div>
                            <p className="text-sm text-yellow-600 mt-1">
                              The current online meeting link will no longer be
                              available if you save these changes.
                            </p>
                          </div>
                        )}
                    </>
                  )}
                </div>
              )}

              {isMeetingRoom ? (
                <div className="mt-2 text-left">
                  <Label htmlFor="guests">Add Guests</Label>
                  <ReserveFieldUsers
                    form={form}
                    defaultValue={defaultValues.users}
                    filterValues={(option) =>
                      option.value !== authenticatedUser?.user?.id
                    }
                    errors={form.formState.errors}
                  />
                </div>
              ) : null}

              {enableFloorSelector ? (
                <div className="mt-2">
                  <Label htmlFor="floor">Location & workspace</Label>
                  <FloorFilterDropdown
                    floors={floors ?? []}
                    floor={filterFloor}
                    setFloor={setFilterFloor}
                    readonly={false}
                    className="border-0 bg-neutral-50"
                  />
                </div>
              ) : null}

              <div className="relative mt-3 text-left">
                {defaultResource ? (
                  <ReserveFieldResourcesList
                    form={form}
                    errors={form.formState.errors}
                    resourceType={entity}
                    officeId={primaryResource.office_id}
                    resourcesList={resourcesList}
                    defaultValue={defaultResource}
                  />
                ) : null}
              </div>
              <div className="mt-3">
                {!isMeetingRoom &&
                !isPrimaryParking &&
                office &&
                office.total_parking_spaces > 0 ? (
                  <div className="mb-5">
                    <ReserveFieldParkingList
                      noParkingResource={noParkingResource}
                      parkingResourcesList={parkingResourcesList}
                      defaultValue={defaultParking}
                      form={form}
                      errors={form.formState.errors}
                    />
                  </div>
                ) : null}
              </div>

              {isMeetingRoom ? (
                <div className="mt-1 mb-2">
                  <button
                    className="flex items-center pb-2 text-secondary-foreground"
                    onClick={(e) => {
                      e.preventDefault();
                      setNotesOpened((s) => !s);
                    }}
                  >
                    <PencilAltIcon className="mr-1.5 h-4 w-4" />
                    Meeting notes
                  </button>
                  {notesOpened && <ReserveFieldDescription form={form} />}
                </div>
              ) : null}

              {!isMeetingRoom ? (
                <div className={cn("mt-2", !allowOthers && "hidden")}>
                  {allowOthers ? (
                    <div className="flex items-center">
                      <span className="font-medium text-neutral-700">User</span>
                      <InfoTooltip position="top">
                        Make this reservation on behalf of another user
                      </InfoTooltip>
                    </div>
                  ) : null}
                  <ReserveFieldUsers
                    single={true}
                    form={form}
                    defaultValue={users}
                    errors={form.formState.errors}
                    readonly={!allowOthers}
                  />
                </div>
              ) : (
                <div className={cn("mt-2", !allowOthers && "hidden")}>
                  {allowOthers ? (
                    <div className="flex items-center">
                      <Label htmlFor="organizer">Organizer</Label>
                      <InfoTooltip position="top">
                        Select an organizer for the meeting. As you are creating
                        this reservation on behalf of another user, you will not
                        be the organizer, nor one of the invitees.
                      </InfoTooltip>
                    </div>
                  ) : null}
                  <ReserveFieldOrganizer
                    form={form}
                    defaultValue={
                      organizer
                        ? organizer
                        : users
                          ? { value: users[0] }
                          : undefined
                    }
                    errors={form.formState.errors}
                    readonly={!allowOthers}
                    showDelegatedAccessWarning={
                      organizerRequiresDelegatedAccess
                    }
                  />
                </div>
              )}

              <ReserveEditFormWPRequests
                ref={wpRequestsRef}
                schedule={schedule}
                officeId={primaryResource.office_id}
                form={form}
              />

              <div className="pb-4 text-red-600 text-sm">{error}</div>

              <Button
                type="submit"
                className="w-full"
                variant="default"
                disabled={loading || organizerRequiresDelegatedAccess}
              >
                {loading ? (
                  <LoadingSpinner color="text-white" />
                ) : (
                  <span>Save Changes</span>
                )}
              </Button>
              {canRespondCancel && (
                <Button
                  type="button"
                  variant="destructiveOutline"
                  disabled={loading}
                  isLoading={loading}
                  onClick={respondCancel}
                  className="mt-2"
                >
                  {!loading && <TrashIcon className="h-5 w-5" />}
                  <span>Cancel Reservation</span>
                </Button>
              )}
            </div>
          </form>
        </Form>
        {confirmFunction ? (
          <ReservationRecurringEditConfirm
            open={!!confirmFunction}
            onClose={() => {
              setConfirmFunction(null);
            }}
            confirmFunction={confirmFunction}
          />
        ) : null}
      </div>
    </TooltipProvider>
  );
};
